/* @flow */
import { groupBy, isEmpty, size, concat, compact } from 'lodash';
import pluralize from 'pluralize';
import * as React from 'react';

import type {
  Organization,
  DistrictStatus,
  UploadError,
  SchoolMapping,
} from 'admin/components/AdminDistrictSisDashboard.jsx';
import AdminNoticeBanner from 'admin/components/AdminNoticeBanner';
import Button from 'components/Button.jsx';
import Copy from 'components/Copy.jsx';
import Heading from 'components/Heading.jsx';
import Link from 'components/Link.jsx';
import LinkConstants from 'constants/LinkConstants';
import Row from 'components/Row.jsx';
import Text from 'components/Text.jsx';
import TitleHeader from 'components/TitleHeader.jsx';

type Props = {
  title: string,
  subtitle: string,
  organization: {
    id: number,
    uuid: string,
    childOrganizations: Array<Organization>,
    sisSyncOverview: {
      districtStatus: DistrictStatus,
    },
  },
};

function numSchoolsToMap(schoolMappings: Array<SchoolMapping>, childOrgs: Array<Organization>) {
  const numPaidChildOrgs = childOrgs.filter(org => org.paidAt).length;
  const numMappedChildOrgs = schoolMappings.filter(mapping => mapping.remindSchoolUuid).length;

  return numPaidChildOrgs - numMappedChildOrgs;
}

function renderMatchingBanner(orgUuid: string, displayNum: number) {
  const button = (
    <Button
      eventElementName="AdminDistrictSisDashboardSftpHeader-matchYourSchools"
      href={`/sftp_sync_setup/matching?district_uuid=${orgUuid}`}>
      Match your schools
    </Button>
  );

  // We might find more schools in spreadsheets than we can map. (ex. not all schools are paid)
  // We might also find that some schools are already mapped.
  // So just take the smallest number and show that.

  return (
    <AdminNoticeBanner glyph="success-checkmark-stroke" color="brand" actionElement={button}>
      {`We’ve detected ${displayNum} ${pluralize(
        'school',
        displayNum
      )} that must be matched before the initial sync can begin.`}
    </AdminNoticeBanner>
  );
}

function renderSetupBanner(orgUuid: string) {
  return (
    <AdminNoticeBanner glyph="success-checkmark-stroke" color="brand" actionElement={null}>
      Get started by entering the SFTP details into your SIS or FTP client. View them in the upper
      right or{' '}
      <Link href={`/sftp_sync_setup/details?district_uuid=${orgUuid}`} weight="semibold">
        click here
      </Link>
      .
    </AdminNoticeBanner>
  );
}

function renderAwaitingFilesBanner() {
  return (
    <AdminNoticeBanner glyph="clock" color="sunrise" actionElement={null}>
      We’re processing your files now. Please check back in 3 minutes.
    </AdminNoticeBanner>
  );
}

function renderUploadErrorsBanner(uploadErrors: Array<UploadError>, filesZipUrl: ?string) {
  function UploadErrorsBannerDetailsList({
    children,
    title,
    element,
  }: {
    children: any,
    title: string,
    element: string,
  }) {
    const Component = element;
    return (
      <React.Fragment>
        <Heading level="3" levelStyle="6">
          {title}
        </Heading>
        <Component>{children}</Component>
      </React.Fragment>
    );
  }

  function UploadErrorsBannerErrorListItem({
    copy,
    errors,
    name,
    element,
  }: {
    copy: any,
    errors: Array<UploadError>,
    name: string,
    element: string,
  }) {
    const Component = element;
    return (
      <Component>
        <p>
          <Text weight="semibold">{name}</Text>: {copy}
        </p>
        <UploadErrorsBannerErrorsFileList errors={errors} />
      </Component>
    );
  }

  function UploadErrorsBannerErrorsFileList({ errors }: { errors: Array<UploadError> }) {
    if (errors.length === 0) {
      return null;
    }
    return (
      <ol>
        {errors.map(item => (
          <React.Fragment>
            <li key={item.fileName}>
              {item.fileName}
              {item.downloadUrl && (
                <p>
                  <Link weight="semibold" href={item.downloadUrl} download>
                    Download CSV of errors
                  </Link>
                </p>
              )}
            </li>
          </React.Fragment>
        ))}
      </ol>
    );
  }

  const groupedErrors = groupBy(uploadErrors, 'error');
  const errorsUnknownFormat = groupedErrors['unknown_format'];
  const errorsDuplicateIds = groupedErrors['duplicate_ids'];
  const errorsInvalid = groupedErrors['invalid'];
  const errorsMismatchedIds = groupedErrors['mismatched_ids'];
  const errorsMissingIds = groupedErrors['missing_ids'];
  const errorsInvalidColumnsForRow = groupedErrors['invalid_columns_for_row'];

  // For clarity, we group some types of errors for the user.
  // There is a chance that no errors with the specified key exist,
  // so we need to remove null values
  const errorsEmpty = compact(concat(groupedErrors['empty'], groupedErrors['incorrect_headers']));
  const errorsMissing = compact(concat(groupedErrors['missing'], groupedErrors['empty']));

  let containerElement = 'div';
  let errorElement = 'div';

  if (Object.keys(groupedErrors).length > 1) {
    containerElement = 'ol';
    errorElement = 'li';
  }

  return (
    <AdminNoticeBanner
      color="lava"
      glyph="explanation-circle"
      actionElement={null}
      details={
        <Copy>
          <Row size="medium">
            <UploadErrorsBannerDetailsList
              title={pluralize('Error', uploadErrors.length)}
              element={containerElement}>
              {!isEmpty(errorsUnknownFormat) && (
                <UploadErrorsBannerErrorListItem
                  name="Unknown Format"
                  element={errorElement}
                  copy={
                    <React.Fragment>
                      We’re unable to determine which CSV format you’re using. Upload a schools.csv
                      if you are following the Remind CSV format, or upload both a manifest.csv and
                      orgs.csv if you are following the OneRoster CSV format. If you sent a zip
                      file, please resend each CSV file separately.
                    </React.Fragment>
                  }
                  errors={[]}
                />
              )}
              {!isEmpty(errorsInvalidColumnsForRow) && (
                <UploadErrorsBannerErrorListItem
                  name="Formatting Error"
                  element={errorElement}
                  copy={
                    <React.Fragment>
                      1 or more of your rows has an extra value, most likely due to a stray comma.
                      Review and remove any stray commas.
                    </React.Fragment>
                  }
                  errors={errorsInvalidColumnsForRow}
                />
              )}
              {!isEmpty(errorsInvalid) && (
                <UploadErrorsBannerErrorListItem
                  name="Unreadable Encoding"
                  element={errorElement}
                  copy={
                    <React.Fragment>
                      We’re unable to read the {pluralize('file', size(errorsInvalid))} you
                      uploaded.
                    </React.Fragment>
                  }
                  errors={errorsInvalid}
                />
              )}
              {!isEmpty(errorsEmpty) && (
                <UploadErrorsBannerErrorListItem
                  name="Invalid Content"
                  element={errorElement}
                  copy={
                    <React.Fragment>
                      The content in the {pluralize('file', size(errorsEmpty))} isn’t correct.{' '}
                      {errorsEmpty.length > 1
                        ? 'Make sure they contain the appropriate headers.'
                        : 'Make sure it contains the appropriate headers.'}
                    </React.Fragment>
                  }
                  errors={errorsEmpty}
                />
              )}
              {!isEmpty(errorsMissing) && (
                <UploadErrorsBannerErrorListItem
                  name="Missing Files"
                  element={errorElement}
                  copy={
                    <React.Fragment>
                      We were unable to locate the following{' '}
                      {pluralize('file', size(errorsMissing))}.
                    </React.Fragment>
                  }
                  errors={errorsMissing}
                />
              )}
              {!isEmpty(errorsDuplicateIds) && (
                <UploadErrorsBannerErrorListItem
                  name="Duplicate IDs"
                  element={errorElement}
                  copy={
                    <React.Fragment>
                      We’ve detected duplicate reference IDs in the following{' '}
                      {pluralize('file', size(errorsDuplicateIds))}. Each ID must be unique.
                    </React.Fragment>
                  }
                  errors={errorsDuplicateIds}
                />
              )}
              {!isEmpty(errorsMissingIds) && (
                <UploadErrorsBannerErrorListItem
                  name="Missing IDs"
                  element={errorElement}
                  copy={
                    <React.Fragment>
                      We were unable to find expected IDs in the following{' '}
                      {pluralize('file', size(errorsMissingIds))}.
                    </React.Fragment>
                  }
                  errors={errorsMissingIds}
                />
              )}
              {!isEmpty(errorsMismatchedIds) && (
                <UploadErrorsBannerErrorListItem
                  name="Mismatched IDs"
                  element={errorElement}
                  copy="We’ve encountered conflicts in IDs across the following files. Ensure they’re consistent."
                  errors={errorsMismatchedIds}
                />
              )}
            </UploadErrorsBannerDetailsList>
          </Row>
          <UploadErrorsBannerDetailsList title="Next steps" element="ol">
            <li>
              <p>
                <Link weight="semibold" href={filesZipUrl} download>
                  Download the files we received.
                </Link>
              </p>
            </li>
            <li>
              <p>
                Refer to{' '}
                <Link weight="semibold" href={LinkConstants.DIRECT_SYNC}>
                  this support article
                </Link>{' '}
                and make sure your CSVs are configured correctly.
              </p>
            </li>
            <li>
              <p>Upload all the CSVs, with revisions, via SFTP again.</p>
            </li>
          </UploadErrorsBannerDetailsList>
        </Copy>
      }>
      We’ve encountered an error with one or more of your CSVs.
    </AdminNoticeBanner>
  );
}

export default function AdminDistrictSisDashboardSftpHeader(props: Props) {
  const { title, subtitle, organization } = props;
  const {
    childOrganizations,
    sisSyncOverview: { districtStatus },
    uuid: orgUuid,
  } = organization;
  const { schoolMappings, awaitingFiles, filesZipUrl, uploadErrors } = districtStatus;

  function renderBanner() {
    const numSchoolsToBeMapped = numSchoolsToMap(schoolMappings, childOrganizations);

    const noSchoolsMapped = schoolMappings.length === 0;
    const someSftpSchoolsNotMapped = numSchoolsToBeMapped > 0;
    const hasUploadErrors = uploadErrors.length !== 0;

    if (hasUploadErrors) {
      return renderUploadErrorsBanner(uploadErrors, filesZipUrl);
    } else if (awaitingFiles) {
      return renderAwaitingFilesBanner();
    } else if (noSchoolsMapped) {
      return renderSetupBanner(orgUuid);
    } else if (someSftpSchoolsNotMapped) {
      return renderMatchingBanner(orgUuid, numSchoolsToBeMapped);
    } else {
      return null;
    }
  }

  return (
    <TitleHeader
      title={title}
      subtext={subtitle}
      action={
        <Button level="secondary" href={`/sftp_sync_setup/details?district_uuid=${orgUuid}`}>
          Set up Direct sync
        </Button>
      }
      actionPlacement="right"
      className="AdminDistrictSisDashboardSftpHeader">
      {renderBanner()}
    </TitleHeader>
  );
}
